import { useForm } from 'react-hook-form'
import { valibotResolver } from '@hookform/resolvers/valibot'
import * as v from 'valibot'
import { Button, Container, Form, Row, Col } from 'react-bootstrap'
import styles from './addSpending.module.css'
import { Select } from '../../common/select/Select'
import { CustomToast } from '../../common/customToast/CustomToast'
import { useAddSpendingMutation } from '../../../features/api/expense/expenseApi'
import { Input } from '../../common/input/Input'
import { useGetAllSpendingCategoryQuery } from '../../../features/api/expense/spendingCategoryApi'
import { useMemo, useState } from 'react'
import { CustomDatePicker } from '../../common/customDatePicker/CustomDatePicker'
import { useGetAllUserQuery } from '../../../features/api/user/userApi'

let paymentMethodList = ['CASH', 'BANK_TRANSFER', 'CHECK']

const schema = v.object({
  amount: v.number('Amount is required'),
  description: v.string('Description is required'),
  paymentMethod: v.union(
    [v.literal('CASH'), v.literal('CHECK'), v.literal('BANK_TRANSFER')],
    'Payment method is required'
  ),
  expenseDate: v.string('Expense date is required'),
  spendingCategory: v.string('Category is required'),
  spentBy: v.string('Spent by is required'),
})

export const AddSpending = ({ onSuccess }) => {
  const [expenseDate, setExpenseDate] = useState(null)
  const [addSpending, { isLoading, isSuccess, isError, error }] =
    useAddSpendingMutation()
  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    resolver: valibotResolver(schema),
    defaultValues: {
      amount: '',
      description: '',
      expenseDate: '',
      spendingCategory: '',
      spentBy: '',
      paymentMethod: '',
    },
  })

  const { data, isCategoryLoading, isCategoryLoadingError } =
    useGetAllSpendingCategoryQuery()

  const {
    data: spentByData,
    isLoading: isSpentByListLoading,
    isError: spentByDataError,
  } = useGetAllUserQuery()

  // Build Spent By Options dynamically -
  const spentByOptions = useMemo(() => {
    if (!spentByData || !Array.isArray(spentByData)) return []

    const options = spentByData.map((user) => ({
      label: `${user.firstName} ${user.lastName}`,
      value: `${user.firstName} ${user.lastName}`,
    }))

    return [...options]
  }, [spentByData])

  const onSubmit = async (data) => {
    const payload = {
      spentBy: data.spentBy,
      amount: data.amount,
      description: data.description,
      paymentMethod: data.paymentMethod,
      expenseDate: data.expenseDate,
      spendingCategory: data.spendingCategory,
    }
    try {
      await addSpending(payload).unwrap()
      handleSuccess()
    } catch (err) {}
  }

  const handleSuccess = () => {
    reset({
      spentBy: '',
      amount: '',
      description: '',
      paymentMethod: '',
      expenseDate: '',
      spendingCategory: '',
    })
    setTimeout(() => {
      onSuccess()
    }, 3000)
  }

  const formatToYMD = (date) => {
    if (!date) return ''
    return date.toISOString().split('T')[0]
  }
  return (
    <Container>
      {isSuccess && <CustomToast msg={`Expense added successfully`} />}
      {isError && <CustomToast err={error} msg={error.data.errorMessage} />}
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <h4>
            <span>Expense </span>
            Info
          </h4>
          <Col sm={12} md={6}>
            <Input
              label={'Amount'}
              type={'number'}
              withUnit={true}
              field={'amount'}
              register={register}
              defaultValue={''}
              errors={errors}
            />
          </Col>
          <Col sm={12} md={6}>
            <Select
              label='Spent by'
              field='spentBy'
              defaultValue={''}
              register={register}
              options={spentByOptions.map((el) => el)}
              errors={errors}
            />
          </Col>
        </Row>
        <Row>
          <Col sm={12} md={6}>
            <Input
              label={'Description'}
              field={'description'}
              register={register}
              defaultValue={''}
              errors={errors}
            />
          </Col>
          <Col sm={12} md={6}>
            <Select
              label='Category'
              field='spendingCategory'
              register={register}
              options={
                isCategoryLoading
                  ? [
                      {
                        value: '',
                        label: 'Loading categories...',
                      },
                    ]
                  : isCategoryLoadingError
                  ? [{ value: '', label: 'Failed to load categories' }]
                  : data?.map((cat) => ({
                      value: cat.name,
                      label: cat.name,
                    })) ?? []
              }
              errors={errors}
            />
          </Col>
        </Row>
        <Row>
          <Col sm={12} md={6}>
            <Select
              label='Payment Method'
              field='paymentMethod'
              register={register}
              options={
                paymentMethodList?.map((method) => ({
                  value: method,
                  label: method,
                })) ?? []
              }
              errors={errors}
            />
          </Col>

          <Col sm={12} md={6}>
            <section className={styles.spendingDate}>
              <p>Spending date</p>
              <CustomDatePicker
                isFilter={false}
                value={expenseDate}
                onChange={(date) => {
                  setExpenseDate(date)
                  setValue('expenseDate', formatToYMD(date))
                }}
              />
            </section>
          </Col>
        </Row>

        <hr />
        <Row className='justify-content-center'>
          <Col md={12} className='text-center'>
            <Button type='submit' variant='success' disabled={isLoading}>
              Confirm
            </Button>
          </Col>
        </Row>
      </Form>
    </Container>
  )
}
